/* ═══════════════════════════════════════════════════════════════
   TasksTable — the List view: DataTable columns (Status/Priority/Project/
   Assignee/Due are standard Filter-menu fields per Decision 3), grouping,
   bulk actions, right-click row menu (Decision 5).
   ═══════════════════════════════════════════════════════════════ */
import { useMemo, useState } from 'react';
import type { Task, TaskStatus } from '../../../shared/types';
import { DataTable, Column, GroupOption } from '../../components/DataTable';
import type { TableFilterState } from '../../components/filterModel';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import EmptyState from '../../components/EmptyState';
import { isArchived } from '../../lib/archive';
import { rem } from '../../lib/rem';
import { toAddress } from '../../routing/address';
import { openAddress } from '../../routing/router';
import { isWeb } from '../../lib/platform';
import { ClipboardList, AlertTriangle } from 'lucide-react';
import { canDeleteTask, canEditTask } from './taskPermissions';
import { TaskRowMenu, type TaskRowMenuPoint } from './TaskRowMenu';
import { useConfirmDeleteTask } from './useConfirmDeleteTask';
import { STATUS_COLS, PRIORITY_COLORS, PRIORITY_LABELS } from './types';
import type { TasksData } from './useTasksData';

export interface TasksTableProps {
  data: TasksData;
  tasks: Task[];
  perms: string[] | undefined;
  canArchiveTask: boolean;
  filterState: TableFilterState;
  onFilterStateChange: (s: TableFilterState) => void;
  onRowClick: (taskId: string) => void;
}

const priorityOrder: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
const statusOrder: Record<string, number> = { todo: 0, in_progress: 1, review: 2, done: 3 };

export function TasksTable({
  data,
  tasks,
  perms,
  canArchiveTask,
  filterState,
  onFilterStateChange,
  onRowClick,
}: TasksTableProps) {
  const [menu, setMenu] = useState<{ point: TaskRowMenuPoint; task: Task } | null>(null);
  const { requestDelete, confirmDeleteDialog } = useConfirmDeleteTask(data);

  const listColumns = useMemo(
    (): Column<Task>[] => [
      {
        key: 'status',
        label: 'Status',
        width: 110,
        getValue: (t) => statusOrder[t.status] ?? 0,
        filterValue: (t) => t.status,
        csvValue: (t) => STATUS_COLS.find((s) => s.id === t.status)?.label || t.status,
        render: (t) => {
          if (isArchived(t)) {
            return (
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  padding: `${rem(2)} var(--space-2)`,
                  borderRadius: 'var(--radius-lg)',
                  background: 'var(--sa)',
                  color: 'var(--tm)',
                }}
              >
                Archived
              </span>
            );
          }
          const sc = STATUS_COLS.find((s) => s.id === t.status);
          return (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                padding: `${rem(2)} var(--space-2)`,
                borderRadius: 'var(--radius-lg)',
                background: sc ? `color-mix(in srgb, ${sc.color} 18%, transparent)` : undefined,
                color: sc?.color,
              }}
            >
              {sc?.label}
            </span>
          );
        },
        filterOptions: STATUS_COLS.map((s) => ({ value: s.id, label: s.label })),
      },
      {
        key: 'priority',
        label: 'Priority',
        width: 90,
        getValue: (t) => priorityOrder[t.priority] ?? 2,
        filterValue: (t) => t.priority,
        csvValue: (t) => PRIORITY_LABELS[t.priority],
        render: (t) => (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 700,
              color: PRIORITY_COLORS[t.priority],
            }}
          >
            {PRIORITY_LABELS[t.priority]}
          </span>
        ),
        filterOptions: Object.entries(PRIORITY_LABELS).map(([v, l]) => ({ value: v, label: l })),
      },
      {
        key: 'title',
        label: 'Title',
        width: 280,
        filterType: 'none',
        getValue: (t) => t.title,
        render: (t) => (
          <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 500, color: 'var(--tx)' }}>
            {t.title}
          </span>
        ),
      },
      {
        key: 'project',
        label: 'Project',
        width: 160,
        sortable: true,
        getValue: (t) => {
          const pIds = data.getProjectIdsForTask(t.id);
          return pIds
            .map((pid) => data.getProjectName(pid))
            .filter(Boolean)
            .join(', ');
        },
        filterValue: (t) => data.getProjectIdsForTask(t.id),
        filterOptions: data.projects
          .filter((p) => p.status === 'active' && !isArchived(p))
          .map((p) => ({
            value: p.id,
            label: p.projectNumber || data.getProjectName(p.id) || 'Untitled',
          })),
        render: (t) => {
          const pIds = data.getProjectIdsForTask(t.id);
          return (
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
              {pIds
                .map((pid) => data.getProjectName(pid))
                .filter(Boolean)
                .join(', ')}
            </span>
          );
        },
      },
      {
        key: 'assignees',
        label: 'Assignees',
        width: 130,
        sortable: false,
        filterValue: (t) => data.getAssigneesForTask(t.id).map((m) => m.id),
        filterOptions: data.members
          .filter((m) => m.active)
          .map((m) => ({ value: m.id, label: m.name })),
        render: (t) => {
          const assignees = data.getAssigneesForTask(t.id);
          return (
            <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
              {assignees.map((m) => (
                <span
                  key={m.id}
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    padding: `${rem(1)} ${rem(6)}`,
                    borderRadius: 'var(--radius-lg)',
                    background: 'var(--sa)',
                    color: 'var(--tm)',
                  }}
                >
                  {m.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: 'dueDate',
        label: 'Due',
        width: 100,
        filterType: 'date-range',
        getValue: (t) => t.dueDate || '9999',
        render: (t) => {
          const isOverdue = t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'done';
          return (
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: isOverdue ? 'var(--dg)' : 'var(--tm)',
                fontWeight: isOverdue ? 600 : 400,
              }}
            >
              {t.dueDate
                ? new Date(t.dueDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })
                : ''}
            </span>
          );
        },
      },
      {
        key: 'subtasks',
        label: 'Subtasks',
        width: 80,
        sortable: false,
        filterType: 'none',
        render: (t) => {
          const sc = data.getSubtaskCount(t.id);
          const sd = data.getSubtasksDone(t.id);
          return (
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
              {sc > 0 ? `${sd}/${sc}` : ''}
            </span>
          );
        },
      },
    ],
    [data.tasks, data.assignments, data.members, data.projects, data.taskProjectLinks]
  );

  const listGroupOptions = useMemo(
    (): GroupOption<Task>[] => [
      {
        key: 'status',
        label: 'Status',
        getValue: (t) => STATUS_COLS.find((s) => s.id === t.status)?.label || t.status,
      },
      {
        key: 'priority',
        label: 'Priority',
        getValue: (t) => PRIORITY_LABELS[t.priority] || t.priority,
      },
      {
        key: 'project',
        label: 'Project',
        getValue: (t) => {
          const pIds = data.getProjectIdsForTask(t.id);
          return pIds.length > 0
            ? pIds
                .map((pid) => data.getProjectName(pid))
                .filter(Boolean)
                .join(', ') || 'No Project'
            : 'No Project';
        },
      },
      {
        // Keyed to the 'assignees' COLUMN (DataTable merges by key) — this
        // used to be keyed 'assignee' (singular), which matched no column
        // and was silently dropped from the group menu.
        key: 'assignees',
        label: 'Assignee',
        getValue: (t) => {
          const assignees = data.getAssigneesForTask(t.id);
          return assignees.length > 0 ? assignees.map((m) => m.name).join(', ') : 'Unassigned';
        },
      },
    ],
    [data.tasks, data.assignments, data.members, data.projects, data.taskProjectLinks]
  );

  function moveTo(taskId: string, status: TaskStatus) {
    void data.handleStatusChange(taskId, status);
  }

  if (data.loadError) {
    return (
      <div style={{ flex: 1, display: 'flex' }}>
        <EmptyState
          icon={<Icon icon={AlertTriangle} em={2} aria-hidden />}
          title="Tasks did not load"
          subtitle="Check your connection and try again."
          action={{ label: 'Try again', onClick: () => void data.retryLoad() }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        overflow: 'hidden',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <DataTable<Task>
        columns={listColumns}
        data={tasks}
        keyField="id"
        onRowClick={(t) => onRowClick(t.id)}
        rowHref={(t) => toAddress({ view: 'tasks', taskId: t.id })}
        onRowContextMenu={(t, e) => setMenu({ point: { x: e.clientX, y: e.clientY }, task: t })}
        groupOptions={listGroupOptions}
        filterState={filterState}
        onFilterStateChange={onFilterStateChange}
        hideSearchBox
        initialSort={{ key: 'status', direction: 'asc' }}
        emptyIcon={<Icon icon={ClipboardList} em={2} aria-hidden />}
        emptyTitle="No tasks"
        emptyMessage="No tasks found"
        storageKey="tasks-list"
        permissions={perms}
        bulkActions={{
          delete: {
            permission: 'tasks.delete',
            onDelete: async (items) => {
              await data.bulkDeleteTasks(items);
            },
            confirmTitle: 'Delete tasks',
            confirmDescription: (count) =>
              count === 1
                ? 'Delete this task and all subtasks?'
                : `Delete these ${count} tasks and all their subtasks?`,
          },
          archive: {
            permission: 'tasks.archive',
            onArchive: (items) => data.doArchiveTasks(items, true),
            onUndo: (items) => data.doArchiveTasks(items, false),
            label: 'Archive',
          },
          exportCsv: { permission: 'tasks.export', filename: 'tasks' },
          extra: (items, meta) => {
            const archivedOnes = items.filter((t) => isArchived(t));
            if (!archivedOnes.length || !canArchiveTask) return null;
            return (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void data.doArchiveTasks(archivedOnes, false);
                  meta?.clear();
                }}
              >
                Restore {archivedOnes.length}
              </Button>
            );
          },
        }}
      />

      {menu && (
        <TaskRowMenu
          point={menu.point}
          currentStatus={menu.task.status}
          isArchived={isArchived(menu.task)}
          canEdit={canEditTask(
            perms,
            data.userId,
            data.members,
            menu.task,
            data.assignments,
            data.tasks
          )}
          canArchive={canArchiveTask}
          canDelete={canDeleteTask(perms)}
          onClose={() => setMenu(null)}
          onOpen={() => onRowClick(menu.task.id)}
          onOpenNewWindow={
            isWeb
              ? undefined
              : () =>
                  openAddress(toAddress({ view: 'tasks', taskId: menu.task.id }), {
                    newWindow: true,
                  })
          }
          onMoveTo={(status) => moveTo(menu.task.id, status)}
          onArchiveToggle={() => void data.doArchiveTasks([menu.task], !isArchived(menu.task))}
          onDelete={() => requestDelete(menu.task)}
        />
      )}

      {confirmDeleteDialog}
    </div>
  );
}
